/**
 * QuoteTermsTab.jsx — Settings tab: edit the quote terms library
 *
 * Two-column layout:
 *  - Left: searchable list of all library terms, grouped by type (core /
 *    conditional / optional / library-added). Click a row to select.
 *  - Right: sticky editor pane with live preview + Save / Reset / Delete.
 *
 * Storage:
 *  - Reads `getEffectiveLibrary(settings)` (DEFAULT_TERMS overlaid with
 *    `settings.data.quoteTerms` overrides).
 *  - Writes back to `settings.data.quoteTerms` (array of override / library-
 *    added entries). Default-term overrides only store changed fields; library-
 *    added entries store the full shape including `isCustomLibrary: true`.
 *
 * Immutability:
 *  - Edits affect new quote builders + drafts. Historical sent/accepted quotes
 *    are frozen (their bodies live in the per-quote snapshot).
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { useToast } from '../layout/Toast';
import { getEffectiveLibrary } from '../../lib/quoteTerms';

// Helper: row-level color dot per term type (matches builder visual language)
const TYPE_DOT = {
  core: 'bg-text-dim',
  conditional: 'bg-amber-400',
  optional: 'bg-blue-400',
  custom: 'bg-indigo-400',
};

const TYPE_LABEL = {
  core: 'Always-on',
  conditional: 'Auto-included when triggered',
  optional: 'Optional (off by default)',
};

const TYPE_GROUP_DESC = {
  core: 'Included on every quote by default. Toggle off per-quote in the builder if needed.',
  conditional: 'Auto-shown when the matching service category is selected on a quote.',
  optional: 'Off by default. Users opt-in per-quote in the builder.',
};

const inputCls =
  'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent';

export default function QuoteTermsTab() {
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const { toast } = useToast();

  const library = useMemo(() => getEffectiveLibrary(settings), [settings]);

  // ── Editor state ──
  const [selectedId, setSelectedId] = useState(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [draftType, setDraftType] = useState('optional');
  const [draftTriggers, setDraftTriggers] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const [search, setSearch] = useState('');
  const bodyRef = useRef(null);

  const selected = useMemo(
    () => library.find((t) => t.id === selectedId) || null,
    [library, selectedId]
  );

  // Populate draft fields whenever the selection changes
  useEffect(() => {
    if (!selected) return;
    setDraftLabel(selected.label || '');
    setDraftBody(selected.body || '');
    setDraftNote(selected.note || '');
    setDraftType(selected.type || 'optional');
    setDraftTriggers(
      Array.isArray(selected.triggerCategories) ? selected.triggerCategories.join(', ') : ''
    );
    setSavedFlash(false);
  }, [selectedId, selected]);

  // ── Filtering ──
  const filtered = useMemo(() => {
    if (!search.trim()) return library;
    const q = search.trim().toLowerCase();
    return library.filter(
      (t) =>
        (t.label || '').toLowerCase().includes(q) ||
        (t.body || '').toLowerCase().includes(q) ||
        (t.id || '').toLowerCase().includes(q)
    );
  }, [library, search]);

  // Group by section. Library-added optional terms fall into "optional" but
  // get the "Library-added" badge inline so they're still distinguishable.
  const grouped = useMemo(() => {
    const buckets = { core: [], conditional: [], optional: [] };
    for (const t of filtered) {
      const key = buckets[t.type] ? t.type : 'optional';
      buckets[key].push(t);
    }
    return buckets;
  }, [filtered]);

  // Dirty flag — disables Save when nothing changed
  const isDirty = useMemo(() => {
    if (!selected) return false;
    if (draftLabel !== (selected.label || '')) return true;
    if (draftBody !== (selected.body || '')) return true;
    if (draftNote !== (selected.note || '')) return true;
    if (selected.isCustomLibrary) {
      if (draftType !== (selected.type || 'optional')) return true;
      const currentTriggers = Array.isArray(selected.triggerCategories)
        ? selected.triggerCategories.join(', ')
        : '';
      if (draftTriggers !== currentTriggers) return true;
    }
    return false;
  }, [selected, draftLabel, draftBody, draftNote, draftType, draftTriggers]);

  // ── Save / Reset / Delete ──
  const handleSave = async () => {
    if (!selected) return;
    try {
      const existing = Array.isArray(settings?.data?.quoteTerms)
        ? settings.data.quoteTerms
        : [];
      const triggers = draftTriggers
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const next = selected.isCustomLibrary
        ? {
            id: selected.id,
            isCustomLibrary: true,
            label: draftLabel,
            body: draftBody,
            note: draftNote,
            type: draftType,
            triggerCategories: triggers,
          }
        : {
            id: selected.id,
            label: draftLabel,
            body: draftBody,
            note: draftNote,
            ...(triggers.length > 0 ? { triggerCategories: triggers } : {}),
          };
      const filteredExisting = existing.filter((t) => t.id !== selected.id);
      await updateSettings.mutateAsync({
        ...settings,
        data: { ...settings.data, quoteTerms: [...filteredExisting, next] },
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const handleReset = async () => {
    if (!selected || selected.isCustomLibrary) return;
    try {
      const existing = Array.isArray(settings?.data?.quoteTerms)
        ? settings.data.quoteTerms
        : [];
      const filteredExisting = existing.filter((t) => t.id !== selected.id);
      await updateSettings.mutateAsync({
        ...settings,
        data: { ...settings.data, quoteTerms: filteredExisting },
      });
      toast('Reset to default');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const handleDelete = async () => {
    if (!selected?.isCustomLibrary) return;
    if (
      !confirm(
        `Delete library term "${selected.label || 'Untitled'}"? It will disappear from new quote builders. Historical quotes are unaffected.`
      )
    )
      return;
    try {
      const existing = Array.isArray(settings?.data?.quoteTerms)
        ? settings.data.quoteTerms
        : [];
      const filteredExisting = existing.filter((t) => t.id !== selected.id);
      await updateSettings.mutateAsync({
        ...settings,
        data: { ...settings.data, quoteTerms: filteredExisting },
      });
      toast('Library term deleted');
      setSelectedId(null);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const handleAdd = async () => {
    try {
      const existing = Array.isArray(settings?.data?.quoteTerms)
        ? settings.data.quoteTerms
        : [];
      const maxLib = existing.reduce((m, t) => {
        const match = String(t?.id || '').match(/^lib_(\d+)$/);
        return match ? Math.max(m, parseInt(match[1], 10)) : m;
      }, 0);
      const newId = `lib_${maxLib + 1}`;
      const newTerm = {
        id: newId,
        isCustomLibrary: true,
        label: 'New library term',
        body: '',
        note: '',
        type: 'optional',
        triggerCategories: [],
        on: false,
      };
      await updateSettings.mutateAsync({
        ...settings,
        data: { ...settings.data, quoteTerms: [...existing, newTerm] },
      });
      setSelectedId(newId);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e) => {
      if (!selected) return;
      // Cmd/Ctrl + S = save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && !updateSettings.isPending) handleSave();
        return;
      }
      // Escape = clear selection (cancel without losing other context)
      if (e.key === 'Escape') {
        // Only intercept if the editor pane has focus to avoid hijacking other UI
        if (document.activeElement?.closest?.('[data-editor-pane]')) {
          setSelectedId(null);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, isDirty, draftLabel, draftBody, draftNote, draftType, draftTriggers, updateSettings.isPending]);

  // Auto-grow body textarea
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(120, el.scrollHeight)}px`;
  }, [draftBody, selectedId]);

  // ── Row component ──
  const TermRow = ({ term }) => {
    const isSelected = term.id === selectedId;
    return (
      <button
        type="button"
        onClick={() => setSelectedId(term.id)}
        className={`w-full text-left rounded border px-3 py-2.5 transition-colors ${
          isSelected
            ? 'border-accent/60 bg-accent/5'
            : 'border-border hover:border-accent/30 bg-surface hover:bg-surface-2'
        }`}
      >
        <div className="flex items-start gap-2.5">
          <span
            className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${TYPE_DOT[term.type] || TYPE_DOT.optional}`}
            title={TYPE_LABEL[term.type] || ''}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-text-primary truncate">
                {term.label || <em className="text-text-dim not-italic">Untitled</em>}
              </span>
              {term.isOverridden && (
                <span className="text-2xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                  Customized
                </span>
              )}
              {term.isCustomLibrary && (
                <span className="text-2xs px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/30">
                  Library-added
                </span>
              )}
              {term.type === 'conditional' &&
                Array.isArray(term.triggerCategories) &&
                term.triggerCategories.length > 0 && (
                  <span className="text-2xs text-text-dim">
                    · {term.triggerCategories.join(', ')}
                  </span>
                )}
            </div>
            <p className="text-2xs text-text-dim leading-snug mt-0.5 line-clamp-2">
              {term.body || <em>No body yet</em>}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // ── Section header with count ──
  const SectionHeader = ({ type }) => (
    <div className="px-1 pt-2 pb-1">
      <div className="flex items-baseline gap-2">
        <span className="text-2xs font-semibold text-text-muted uppercase tracking-wider">
          {TYPE_LABEL[type] || type}
        </span>
        <span className="text-2xs text-text-dim">{grouped[type].length}</span>
      </div>
      <p className="text-2xs text-text-dim leading-snug mt-0.5">{TYPE_GROUP_DESC[type]}</p>
    </div>
  );

  const trimmedTriggers = draftTriggers
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="space-y-3">
      <p className="text-text-dim text-sm">
        The terms library is the starting point every quote builder loads from. Edits here change
        the body that appears for <strong className="text-text-primary">new quotes</strong>.
        Historical sent/accepted quotes are frozen and unaffected.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-4">
        {/* ── Left: list ── */}
        <div className="space-y-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search terms…"
            className={inputCls}
          />

          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            {filtered.length === 0 ? (
              <div className="text-xs text-text-dim italic p-4 text-center border border-dashed border-border rounded">
                No terms match "{search}"
              </div>
            ) : (
              ['core', 'conditional', 'optional'].map((type) => {
                if (grouped[type].length === 0) return null;
                return (
                  <div key={type} className="space-y-1.5">
                    <SectionHeader type={type} />
                    {grouped[type].map((term) => (
                      <TermRow key={term.id} term={term} />
                    ))}
                  </div>
                );
              })
            )}
          </div>

          <button
            onClick={handleAdd}
            disabled={updateSettings.isPending}
            className="w-full px-3 py-2.5 text-xs font-medium bg-surface border border-dashed border-accent/40 text-accent rounded hover:bg-accent/10 transition-colors disabled:opacity-50"
          >
            + Add Library Term
          </button>
        </div>

        {/* ── Right: editor ── */}
        <div className="lg:sticky lg:top-2 lg:self-start">
          {!selected ? (
            <div className="border border-dashed border-border rounded-lg p-8 text-center bg-surface/40">
              <p className="text-text-dim text-sm">Select a term to view or edit it.</p>
              <p className="text-2xs text-text-dim mt-1">
                Or click <span className="text-accent">+ Add Library Term</span> to create a new one.
              </p>
            </div>
          ) : (
            <div
              data-editor-pane
              className="border border-border rounded-lg bg-surface p-4 space-y-3"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`w-2 h-2 rounded-full ${TYPE_DOT[selected.type] || TYPE_DOT.optional}`}
                    />
                    <h3 className="text-sm font-semibold text-text-primary truncate">
                      {draftLabel || 'Untitled'}
                    </h3>
                    {selected.isOverridden && (
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                        Customized
                      </span>
                    )}
                    {selected.isCustomLibrary && (
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/30">
                        Library-added
                      </span>
                    )}
                  </div>
                  <p className="text-2xs text-text-dim mt-1">
                    {TYPE_LABEL[selected.type] || selected.type}
                    {selected.type === 'conditional' && trimmedTriggers.length > 0 && (
                      <> — triggers: <code className="text-accent">{trimmedTriggers.join(', ')}</code></>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedId(null)}
                  className="text-text-dim hover:text-text-primary text-lg leading-none px-2 -mt-1"
                  aria-label="Close editor"
                  title="Close (Esc)"
                >
                  ×
                </button>
              </div>

              {/* Label */}
              <div>
                <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                  Label
                </label>
                <input
                  type="text"
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  className={inputCls}
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                  Body
                </label>
                <textarea
                  ref={bodyRef}
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  rows={6}
                  className={`${inputCls} leading-relaxed resize-none`}
                />
              </div>

              {/* Internal note (portal-only) */}
              <div>
                <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                  Internal note
                </label>
                <textarea
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  rows={2}
                  placeholder="Reminder for your team — e.g. when to use this term"
                  className={`${inputCls} leading-relaxed resize-none`}
                />
                <p className="text-2xs text-text-dim mt-1 leading-snug">
                  Visible only in the portal. Never shown on the quote PDF or email.
                </p>
              </div>

              {/* Type + triggers (library-added only) */}
              {selected.isCustomLibrary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                      Type
                    </label>
                    <select
                      value={draftType}
                      onChange={(e) => setDraftType(e.target.value)}
                      className={inputCls}
                    >
                      <option value="core">Always-on</option>
                      <option value="conditional">Auto-included when triggered</option>
                      <option value="optional">Optional (off by default)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                      Trigger categories
                    </label>
                    <input
                      type="text"
                      value={draftTriggers}
                      onChange={(e) => setDraftTriggers(e.target.value)}
                      placeholder={draftType === 'conditional' ? 'e.g. drone, aerial' : '(not used)'}
                      className={inputCls}
                      disabled={draftType !== 'conditional'}
                    />
                    {draftType === 'conditional' && (
                      <p className="text-2xs text-text-dim mt-1 leading-snug">
                        Comma-separated category IDs. Common values:{' '}
                        <code className="text-text-muted">photo</code>,{' '}
                        <code className="text-text-muted">drone</code>,{' '}
                        <code className="text-text-muted">video</code>,{' '}
                        <code className="text-text-muted">floor</code>,{' '}
                        <code className="text-text-muted">film</code>,{' '}
                        <code className="text-text-muted">aerial</code>.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Live preview */}
              <div>
                <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                  Preview
                </label>
                <div className="border border-border rounded bg-surface-2/50 px-3 py-2.5 space-y-1.5">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-text-primary">
                    {draftLabel || 'Untitled'}
                  </div>
                  <div className="text-2xs text-text-dim leading-relaxed whitespace-pre-wrap">
                    {draftBody || <em>No body</em>}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50">
                <button
                  onClick={handleSave}
                  disabled={!isDirty || updateSettings.isPending}
                  className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {updateSettings.isPending
                    ? 'Saving…'
                    : savedFlash
                      ? 'Saved ✓'
                      : 'Save'}
                </button>
                {!selected.isCustomLibrary && selected.isOverridden && (
                  <button
                    onClick={handleReset}
                    disabled={updateSettings.isPending}
                    className="px-3 py-2 bg-surface-2 border border-border text-text-muted rounded text-sm hover:bg-surface-3 hover:text-text-primary transition-colors"
                  >
                    Reset to default
                  </button>
                )}
                <span className="ml-auto text-2xs text-text-dim italic">
                  ⌘S to save · Esc to close
                </span>
                {selected.isCustomLibrary && (
                  <button
                    onClick={handleDelete}
                    disabled={updateSettings.isPending}
                    className="px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded text-sm hover:bg-red-500/20 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>

              <p className="text-2xs text-text-dim italic leading-snug">
                Edits affect new quote builders + open drafts. Existing sent / accepted quotes are
                frozen and continue to show their original body.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
